<template>
  <div v-if="canShowPrompt" ref="pushPrompt" class="z-max fixed inset-0 items-center justify-center hidden">
    <div
      @click="closePrompt()"
      class="absolute z-0 w-full h-full duration-200 opacity-0"
      :class="{'bg-black': theme === 'light', 'bg-concrete': theme === 'dark'}"
    ></div>
    <div class="absolute z-1 w-[358px] p-4 flex opacity-0 duration-200 translate-y-2 flex-col gap-4 bg-white rounded-lg">
      <t-heading tag="h2" class="font-semibold text-center" color="black">{{
        $t('components.pushPrompt.title', {appName})
      }}</t-heading>
      <t-text class="text-center" color="black">{{ $t('components.pushPrompt.description') }}</t-text>
      <div v-if="DEV" class="border-t-gray flex flex-wrap items-center justify-center gap-4 pt-4 border-t">
        <div class="bg-concrete flex flex-col items-center w-full gap-1 p-2 rounded-lg">
          <t-text color="black">PushManager in window</t-text>
          <t-text :color="pushManager ? 'green' : 'red'">{{ pushManager ? 'YES' : 'NO' }}</t-text>
        </div>
        <div class="bg-concrete flex flex-col items-center w-full gap-1 p-2 rounded-lg">
          <t-text color="black">Notification in window</t-text>
          <t-text :color="notification ? 'green' : 'red'">{{ notification ? 'YES' : 'NO' }}</t-text>
        </div>
        <div class="bg-concrete flex flex-col items-center w-full gap-1 p-2 rounded-lg">
          <t-text color="black">showNotification in ServiceWorkerRegistration.prototype</t-text>
          <t-text :color="showNotification ? 'green' : 'red'">{{ showNotification ? 'YES' : 'NO' }}</t-text>
        </div>
        <div class="bg-concrete flex flex-col items-center w-full gap-1 p-2 rounded-lg">
          <t-text color="black">Notification.permission === 'default'</t-text>
          <t-text :color="permission ? 'green' : 'red'">{{ permission ? 'YES' : 'NO' }}</t-text>
        </div>
      </div>
      <div class="flex gap-2">
        <t-button
          @click.native="
            handleClosing()
            closePrompt()
          "
        >
          <t-text class="font-semibold">{{ $t(`components.pushPrompt.${denyButtonText}`) }}</t-text>
        </t-button>
        <t-button color="branded" @click.native="handlePushState()">
          <t-text class="font-semibold">{{ $t('components.pushPrompt.allow') }}</t-text>
        </t-button>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import {mapMutations} from 'vuex'
  import {mapActions} from 'vuex'
  export default {
    name: 'push-prompt',
    computed: {
      ...mapGetters(['appPublished', 'appName', 'prefetchDone', 'theme', 'appApi']),
      ...mapGetters('user', ['userNotifications', 'userPushPublicKey', 'SWLoaded', 'SW']),
      canShowPrompt() {
        return (
          'PushManager' in window &&
          'Notification' in window &&
          'showNotification' in ServiceWorkerRegistration.prototype &&
          Notification.permission === 'default' &&
          !this.$cookies.get('pushPromptDenied')
        )
      },
      pushManager() {
        return 'PushManager' in window
      },
      notification() {
        return 'Notification' in window
      },
      denyButtonText() {
        return this.$cookies.get('pushPromptDeniedTimes') == 1 ? 'neverLater' : 'later'
      },
      showNotification() {
        return 'showNotification' in ServiceWorkerRegistration.prototype
      },
      permission() {
        return Notification.permission === 'default'
      },
      DEV() {
        return process.env.NODE_ENV === 'development' || this.appApi.split('.')[1] === 'dev'
      }
    },
    methods: {
      ...mapMutations('user', ['setUserNotificationState']),
      ...mapActions('user', ['subscribeToPushNotifications']),
      openPrompt() {
        this.$refs.pushPrompt.classList.replace('hidden', 'flex')
        setTimeout(() => {
          this.$refs.pushPrompt.firstChild.classList.replace('opacity-0', 'opacity-50')
          setTimeout(() => {
            this.$refs.pushPrompt.lastChild.classList.replace('opacity-0', 'opacity-100')
            this.$refs.pushPrompt.lastChild.classList.replace('translate-y-2', 'translate-y-0')
          }, 50)
        }, 0)
      },
      handleClosing() {
        const numberOfCloses = this.$cookies.get('pushPromptDeniedTimes') ? +this.$cookies.get('pushPromptDeniedTimes') : 0
        if (numberOfCloses === 0) {
          this.$cookies.set('pushPromptDeniedTimes', 1)
          this.$cookies.set('pushPromptDenied', true)
        }
        if (numberOfCloses === 1) {
          this.$cookies.set('neverShowPushPrompt', true)
        }
      },
      closePrompt() {
        this.$refs.pushPrompt.lastChild.classList.replace('opacity-100', 'opacity-0')
        this.$refs.pushPrompt.lastChild.classList.replace('translate-y-0', 'translate-y-2')
        setTimeout(() => {
          this.$refs.pushPrompt.firstChild.classList.replace('opacity-50', 'opacity-0')
        }, 50)
        setTimeout(() => {
          this.$refs.pushPrompt.classList.replace('flex', 'hidden')
          this.$forceUpdate()
        }, 250)
      },
      async handlePushState() {
        this.closePrompt()
        await this.SWLoaded
        const subscription = await this.SW.pushManager.getSubscription()
        if (Notification.permission === 'default' || subscription === null) {
          Notification.requestPermission().then(async result => {
            if (result === 'granted') {
              this.SW.pushManager
                .subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: this.$helpers.urlBase64ToUint8Array(this.userPushPublicKey)
                })
                .then(async subscription => {
                  this.closePrompt()
                  await this.subscribeToPushNotifications({subscription, method: 'POST'})
                })
            }
          })
        }
      }
    },
    async mounted() {
      await this.prefetchDone
      await this.SWLoaded
      const neverShowPushPrompt = this.$cookies.get('neverShowPushPrompt')
      setTimeout(() => {
        if (!('PushManager' in window || 'showNotification' in ServiceWorkerRegistration.prototype)) return
        if (this.$cookies.get('pushPromptDenied')) return
        if ('Notification' in window && Notification.permission !== 'default') return
        if (this.appPublished && !neverShowPushPrompt) this.openPrompt()
      }, 1000)
    }
  }
</script>
